import { useMemo } from "react";
import { useEditorStore } from "../store";

const POSITIONAL_PARAM_RE = /\$(\d+)/g;

/** Extract unique positional parameter numbers from SQL text, sorted numerically. */
function extractParamNumbers(text: string): number[] {
  const nums = new Set<number>();
  for (const match of text.matchAll(POSITIONAL_PARAM_RE)) {
    nums.add(Number(match[1]));
  }
  return [...nums].sort((a, b) => a - b);
}

interface SqlParamsPaneProps {
  queryText: string;
}

export function SqlParamsPane({ queryText }: SqlParamsPaneProps) {
  const variables = useEditorStore((s) => s.variables);
  const setVariables = useEditorStore((s) => s.setVariables);

  const paramNumbers = useMemo(() => extractParamNumbers(queryText), [queryText]);

  // Parse current variables as JSON array
  const paramValues = useMemo<string[]>(() => {
    if (!variables.trim()) return [];
    try {
      const parsed = JSON.parse(variables);
      if (Array.isArray(parsed)) {
        return parsed.map((v: unknown) => (typeof v === "string" ? v : JSON.stringify(v)));
      }
    } catch {
      /* not valid JSON yet */
    }
    return [];
  }, [variables]);

  const updateParam = (index: number, value: string) => {
    // Ensure array is large enough for the highest parameter index
    const maxIndex = paramNumbers.length > 0 ? Math.max(...paramNumbers) : 0;
    const next = Array.from({ length: maxIndex }, (_, i) => paramValues[i] ?? "");
    next[index] = value;
    setVariables(JSON.stringify(next));
  };

  if (paramNumbers.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <div className="max-w-xs">
          <p className="text-xs text-zinc-500">
            Use{" "}
            <code className="rounded bg-surface-overlay px-1.5 py-0.5 text-accent">$1</code>
            ,{" "}
            <code className="rounded bg-surface-overlay px-1.5 py-0.5 text-accent">$2</code>
            , ... in your query to add parameters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0 overflow-auto p-2">
      {paramNumbers.map((num) => {
        // $1 → index 0, $2 → index 1, etc.
        const index = num - 1;
        return (
          <div key={num} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-surface-overlay/50">
            <label className="w-32 shrink-0 truncate font-mono text-xs text-accent" title={`$${num}`}>
              {`$${num}`}
            </label>
            <input
              type="text"
              value={paramValues[index] ?? ""}
              onChange={(e) => updateParam(index, e.target.value)}
              placeholder="value"
              className="flex-1 rounded border border-zinc-700 bg-surface px-2 py-1 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
            />
          </div>
        );
      })}
    </div>
  );
}
